import type { QueryClient } from '@tanstack/react-query';
import { notFound } from '@tanstack/react-router';

import { OrderDirection } from '#features/api/types';
import { blogPostsQueryOptions } from '#features/blog/model/blog';
import { getGameSettingsQueryOptions, getGamesQueryOptions } from '#features/game/model/services';
import { landingPageQueryOptions } from '#features/landing/model/landing';
import { landingHead } from '#features/landing/utils/landingHead';

// Landings are indexed on prod. Set back to `true` to hide them from search again
// (e.g. before a relaunch); that also re-adds the "[Preview]" title prefix.
// One-line switch — do NOT scatter noindex per route.
const NOINDEX_UNTIL_LAUNCH = false;

export type LandingRouteLoaderData = {
  title: string | null;
  description: string | null;
  keywords: string | null;
  image: string | null;
  name: string | null;
};

/**
 * Shared loader for the prod landing routes (index + one per game). Mirrors the preview
 * route: warms landing copy + blog + the games list, and — on a game page — the game's
 * admin settings so GameFacets renders on SSR.
 */
export const landingRouteLoader = async (
  queryClient: QueryClient,
  gameSlug: string
): Promise<LandingRouteLoaderData> => {
  const landingDataPromise = queryClient.ensureQueryData(landingPageQueryOptions(gameSlug));
  const blogPostsPromise = queryClient.ensureQueryData(
    blogPostsQueryOptions({
      limit: 3,
      offset: 0,
      orders: [{ direction: OrderDirection.DESC, field: 'date' }],
    })
  );
  const gamesPromise = queryClient.ensureQueryData(getGamesQueryOptions());

  const [landingData, _blogPosts, games] = await Promise.all([
    landingDataPromise,
    blogPostsPromise,
    gamesPromise,
  ]);

  if (!landingData) {
    throw notFound({ routeId: '/{-$locale}/_layout' });
  }

  // Prewarm the game's admin settings (ranks/legends/etc.) so the facets render on SSR.
  const currentGame = games?.find((game) => game?.__typename === 'Game' && game.slug === gameSlug);
  if (currentGame?.__typename === 'Game') {
    await queryClient.ensureQueryData(getGameSettingsQueryOptions({ id: currentGame.id }));
  }

  return {
    title: landingData.title,
    description: landingData.description,
    keywords: landingData.keywords,
    image: landingData.previewImage,
    name: landingData.name,
  };
};

/** Shared head() builder — full SEO (canonical + hreflang + JSON-LD) via landingHead. */
export const landingRouteHead = (
  gameSlug: string,
  loaderData: LandingRouteLoaderData | undefined,
  locale: string | undefined
) => {
  if (!loaderData) {
    return {};
  }
  return landingHead({
    title: loaderData.title,
    description: loaderData.description,
    keywords: loaderData.keywords,
    image: loaderData.image,
    gameSlug,
    gameName: loaderData.name,
    locale,
    noindex: NOINDEX_UNTIL_LAUNCH,
  });
};
